import { useState } from 'react';
import { EditOutlined } from '@ant-design/icons';
import { Button, Form, Input, Space, Typography } from 'antd';
import styled from 'styled-components';
import {
  UserAccountNotificationSettingsResponse,
  UserAccountProfileResponse,
  useGetMyAccount,
  useUpdateMyAccount,
} from '@/shared/api';
import { Col, Row } from '@/shared/ui';
import { captureError } from '@/shared/utils/error-reporting';

const { Title, Text } = Typography;

export const PersonalDetails = () => {
  const [editing, setEditing] = useState(false);

  const { data: accountData, refetch: refetchAccountData } = useGetMyAccount<{
    profile: UserAccountProfileResponse;
    notificationSettings: UserAccountNotificationSettingsResponse;
  }>({});
  const { mutate: updateAccount, isLoading: updateAccountLoading } =
    useUpdateMyAccount();

  const [form] = Form.useForm<{ firstName: string; lastName: string }>();

  const onSubmit = async () => {
    const data = form.getFieldsValue();

    if (data.firstName === '' || data.lastName === '') {
      return;
    }

    if (
      data.firstName === accountData?.profile.firstName &&
      data.lastName === accountData?.profile.lastName
    ) {
      setEditing(false);
      return;
    }

    try {
      updateAccount(
        {
          body: {
            profile: {
              ...data,
            },
          },
        },
        {
          onSuccess: () => {
            refetchAccountData();
            setEditing(false);
          },
        },
      );
    } catch (error) {
      captureError(error, {
        module: 'personal_details_on_error_update_account',
        action: 'update_account',
      });
    }
  };

  return (
    <>
      <Row gap={25}>
        <Title level={3} style={{ marginBottom: 0, whiteSpace: 'nowrap' }}>
          Personal details
        </Title>
        {editing ? (
          <Row>
            <Button
              size="small"
              onClick={() => {
                form.resetFields();
                setEditing(false);
              }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              htmlType="submit"
              type="primary"
              loading={updateAccountLoading}
              onClick={onSubmit}
            >
              Save
            </Button>
          </Row>
        ) : (
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => setEditing(true)}
            style={{ marginTop: 3 }}
          >
            Edit
          </Button>
        )}
      </Row>
      <Space
        direction="vertical"
        size="large"
        style={{ width: '100%', marginTop: 40 }}
      >
        {editing ? (
          <Form
            name="basic"
            form={form}
            layout="vertical"
            initialValues={{
              firstName: accountData?.profile.firstName,
              lastName: accountData?.profile.lastName,
            }}
            autoComplete="off"
            style={{ marginTop: 10 }}
          >
            <Row gap={20} style={{ maxWidth: 500, width: '100%' }}>
              <Form.Item
                label="First Name"
                name="firstName"
                rules={[
                  {
                    required: true,
                    message: 'Please input your first name',
                  },
                ]}
                style={{ width: '100%', marginBottom: 0, height: 80 }}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label="Last Name"
                name="lastName"
                rules={[
                  {
                    required: true,
                    message: 'Please input your last name',
                  },
                ]}
                style={{ width: '100%', marginBottom: 0, height: 80 }}
              >
                <Input />
              </Form.Item>
            </Row>
          </Form>
        ) : (
          <Row>
            <Col alignItems="flex-stat" style={{ width: '50%' }}>
              <Label>First Name</Label>
              <Text>{accountData?.profile.firstName}</Text>
            </Col>
            <Col alignItems="flex-stat" style={{ width: '50%' }}>
              <Label>Last Name</Label>
              <Text>{accountData?.profile.lastName}</Text>
            </Col>
          </Row>
        )}
        <Row>
          <Col alignItems="flex-stat" style={{ width: '50%' }}>
            <Label>Email address</Label>
            <Text>{accountData?.profile.email}</Text>
          </Col>
        </Row>
      </Space>
    </>
  );
};

const Label = styled.div`
  opacity: 0.6;
  font-size: 13px;
`;
